import { useCallback, useMemo, useState } from 'react';
import {
  buildArticleSharePayload,
  copyShareText,
  linkedInShareUrl,
  mailtoShareUrl,
  nativeShareArticle,
  twitterShareUrl,
} from '../../lib/articleShare.js';

function ShareIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function LinkedInIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 4.126 0 2.062 2.062 0 0 1-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function InstagramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * @param {{ article: { title?: string, takeaway?: string, category?: string, webUrl?: string } }} props
 */
export default function BriefArticleShare({ article }) {
  const payload = useMemo(() => buildArticleSharePayload(article), [article]);
  const [feedback, setFeedback] = useState('');

  const handleSend = useCallback(async () => {
    if (!payload.url) return;
    try {
      const shared = await nativeShareArticle(payload);
      if (shared) return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
    window.location.href = mailtoShareUrl(payload.emailSubject, payload.emailBody);
  }, [payload]);

  const handleCopy = useCallback(async () => {
    if (!payload.text) return;
    const ok = await copyShareText(payload.text);
    setFeedback(ok ? 'Copied — paste in Instagram' : 'Copy not supported');
    setTimeout(() => setFeedback(''), 2200);
  }, [payload.text]);

  if (!payload.url) return null;

  const shareButtons = [
    {
      id: 'send',
      label: 'Send to someone',
      onClick: handleSend,
      icon: ShareIcon,
    },
    {
      id: 'instagram',
      label: 'Copy for Instagram',
      onClick: handleCopy,
      icon: InstagramIcon,
    },
    {
      id: 'linkedin',
      label: 'Share on LinkedIn',
      href: linkedInShareUrl(payload.url),
      icon: LinkedInIcon,
    },
    {
      id: 'x',
      label: 'Share on X',
      href: twitterShareUrl(payload.url, payload.tweetText),
      icon: XIcon,
    },
  ];

  return (
    <div className="flex flex-col items-end gap-2">
      <div
        className="flex flex-wrap items-center justify-end gap-2"
        role="group"
        aria-label="Share this article"
      >
        {shareButtons.map(({ id, label, href, onClick, icon: Icon }) =>
          href ? (
            <a
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="hxd-article-share-btn"
              aria-label={label}
              title={label}
            >
              <Icon className="w-[18px] h-[18px]" />
            </a>
          ) : (
            <button
              key={id}
              type="button"
              onClick={onClick}
              className="hxd-article-share-btn"
              aria-label={label}
              title={label}
            >
              <Icon className="w-[18px] h-[18px]" />
            </button>
          )
        )}
      </div>
      {feedback ? (
        <p className="font-sans text-[13px] text-[#525252] m-0" role="status" aria-live="polite">
          {feedback}
        </p>
      ) : null}
    </div>
  );
}
